import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import visibilityService from "../services/visibilityService";
import { useAuth } from "./AuthContext";

export const VisibilityContext = createContext();

/**
 * Provides the current user's profession-scoped feature-access map.
 *
 * Usage: const { canAccess } = useVisibility();
 *        canAccess("VIEW_BONUS")  // → boolean
 *
 * Managers (ADMIN / BERUFSBILDNER) receive fullAccess and canAccess always
 * returns true. While loading, or for any key not present in the map, access
 * defaults to true so the UI never hides features because of a transient load.
 * The backend remains the source of truth and returns 403 on disabled features.
 */
export const VisibilityProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [access, setAccess] = useState({});
  const [fullAccess, setFullAccess] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await visibilityService.getMyVisibility();
      setFullAccess(!!data.fullAccess);
      setAccess(data.access || {});
    } catch (e) {
      // Fail open in the UI; the backend still enforces access.
      setFullAccess(true);
      setAccess({});
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setAccess({});
      setFullAccess(true);
      setLoaded(false);
    }
  }, [isAuthenticated, refresh]);

  const canAccess = useCallback(
    (key) => {
      if (fullAccess) return true;
      if (!(key in access)) return true; // unknown/not-applicable → allowed
      return access[key] !== false;
    },
    [fullAccess, access],
  );

  const value = { access, fullAccess, loaded, canAccess, refresh };

  return (
    <VisibilityContext.Provider value={value}>
      {children}
    </VisibilityContext.Provider>
  );
};

export const useVisibility = () => {
  const context = useContext(VisibilityContext);
  if (!context) {
    // Defensive default so components don't crash outside the provider (e.g. tests).
    return {
      access: {},
      fullAccess: true,
      loaded: true,
      canAccess: () => true,
      refresh: async () => {},
    };
  }
  return context;
};
